import { useMediaQuery } from "@mui/material";
import { Position } from "google-map-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { RestaurantItemType } from "../../../../api/types/restaurant.type";
import { translationsContent } from "../../../../localization/translations";
import { useLanguage } from "../../../../store/lang.context";
import { useMap } from "../../../../store/map.context";
import { useTakeawaySelector } from "../../../../store/takeawaySelector.context";
import { translate } from "../../../../utils/translate";
import GoogleMapsInput from "../../../ui/input/GoogleMapsInput";
import Map from "../Map/index";
import RestaurantList from "../RestaurantList";
import classes from "../restaurants.module.scss";
import styles from "./restaurantMapPage.module.scss";

interface Props {
  restaurants: RestaurantItemType[];
}

const RestaurantMapPage = ({ restaurants }: Props) => {
  const isMobile = useMediaQuery("(max-width: 1280px)");
  const { setMap } = useMap();
  const [listIsOpen, setListIsOpen] = useState(false);

  const { language } = useLanguage();

  // const { restaurants } = useRestaurants();

  const inputRef = useRef<HTMLInputElement>(null);

  const [position, setPosition] = useState<Position>({ lat: 0, lng: 0 });

  const { setTakeawaySelector } = useTakeawaySelector();

  const handleCloseRestaurantsModal = () => {
    setTakeawaySelector((prev) => ({ ...prev, isRestaurantsModalOpen: false }));
  };

  const handleOpenTakeawayModal = () => {
    setTakeawaySelector((prev) => ({ ...prev, isTakeawayModalOpen: true }));
  };

  useEffect(() => {
    if (position.lat !== 0 && position.lng !== 0)
      setMap((prevState) => ({
        ...prevState,
        lat: position.lat,
        lng: position.lng,
      }));
  }, [position, setMap]);

  const sqDist = useCallback(
    (lat: number, lng: number) => {
      return (position?.lat - lat) ** 2 + (position.lng - lng) ** 2;
    },
    [position]
  );

  if (restaurants && position.lat !== 0 && position.lng !== 0) {
    restaurants.sort((a, b) => {
      return (
        sqDist(a.latitude ? a.latitude : 0, a.longitude ? a.longitude : 0) -
        sqDist(b.latitude ? b.latitude : 0, b.longitude ? b.longitude : 0)
      );
    });
  }

  return (
    <>
      {restaurants && !isMobile ? (
        <div className={`${classes.container} ${styles.container}`}>
          <div className={classes.restaurants}>
            <div>
              <GoogleMapsInput
                name="google maps search"
                label={`${translate(
                  translationsContent,
                  "restaurantsHeader",
                  language
                )}`}
                placeholder={`${translate(
                  translationsContent,
                  "enterAddress",
                  language
                )}`}
                setPosition={setPosition}
                position={position}
                inputRef={inputRef}
                restaurants={restaurants}
              />
            </div>
            <div
              className={`${classes.innerContainer} ${styles.innerContainer}`}
            >
              {restaurants && (
                <RestaurantList
                  restaurants={restaurants}
                  setModalIsOpen={handleCloseRestaurantsModal}
                  setIsOpen={handleOpenTakeawayModal}
                  position={position}
                  inputRef={inputRef}
                  scrollIntoView
                />
              )}
            </div>
          </div>
          <div className={classes.map}>
            <Map
              restaurants={restaurants}
              inputRef={inputRef}
              setModalIsOpen={handleCloseRestaurantsModal}
              setIsOpen={handleOpenTakeawayModal}
            />
          </div>
        </div>
      ) : (
        <div className={classes.container}>
          <p className={classes.header}>
            {translate(translationsContent, "ourLocations", language)}
          </p>
          <div className={classes.responsive}>
            <div className={classes.responsiveInput}>
              <GoogleMapsInput
                name="google maps search"
                label={`${translate(
                  translationsContent,
                  "restaurantsHeader",
                  language
                )}`}
                placeholder={`${translate(
                  translationsContent,
                  "enterAddress",
                  language
                )}`}
                setPosition={setPosition}
                position={position}
                inputRef={inputRef}
                restaurants={restaurants}
              />
              <div className={classes.select}>
                <span
                  className={`${!listIsOpen && classes.active}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setListIsOpen(false)}
                >
                  {" "}
                  {translate(translationsContent, "restaurantList", language)}
                </span>

                <span> | </span>
                <span
                  className={`${listIsOpen && classes.active}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setListIsOpen(true)}
                >
                  {translate(translationsContent, "location", language)}{" "}
                </span>
              </div>
            </div>
            {!listIsOpen && restaurants ? (
              <div className={classes.restaurantsContainer}>
                <RestaurantList
                  restaurants={restaurants}
                  position={position}
                  setModalIsOpen={handleCloseRestaurantsModal}
                  setIsOpen={handleOpenTakeawayModal}
                  inputRef={inputRef}
                  scrollIntoView
                />
              </div>
            ) : (
              <div className={`${classes.map}`}>
                <Map
                  restaurants={restaurants}
                  inputRef={inputRef}
                  setModalIsOpen={handleCloseRestaurantsModal}
                  setIsOpen={handleOpenTakeawayModal}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RestaurantMapPage;
